import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { Common as MBDCommon } from 'trackolding/lib/modern';

interface MBDContextType {
  rawComponentName: string;
  vertical: string;
  pageType: string;
  buildMBDParam: (
    componentName: string,
    params?: MBDCommon['string_properties'],
  ) => string;
}

interface MBDContextProviderProps {
  children: ReactNode;
  rawComponentName: string;
  vertical: string;
  pageType: string;
}

const MBDContext = createContext<MBDContextType>({
  rawComponentName: '',
  vertical: '',
  pageType: '',
  buildMBDParam: () => '',
});

// parse component name, for example: INFO_SNIPPET => seo_info_snippet
// SEO_CROSS_LINKS_V2, SEO_CONTENT_BLURB are not added repeatedly
export const parseComponentName = (rawComponentName: string) => {
  const lowerCaseName = rawComponentName.replaceAll('-', '_').toLowerCase();
  if (lowerCaseName.startsWith('seo_')) {
    return lowerCaseName;
  }
  return `seo_${lowerCaseName}`;
};

// create context for each component
export function MBDContextProvider({
  children,
  pageType,
  rawComponentName,
  vertical,
}: MBDContextProviderProps) {
  const mbdProps = useMemo(() => {
    const buildMBDParam = (
      componentName: string,
      params: MBDCommon['string_properties'] = {},
    ) => {
      const mbdParam: MBDCommon = {
        component_name: componentName,
        string_properties: { vertical, page_type: pageType, ...params },
      };
      return JSON.stringify(mbdParam);
    };
    return {
      rawComponentName: parseComponentName(rawComponentName),
      vertical,
      pageType,
      buildMBDParam,
    };
  }, [rawComponentName, vertical, pageType]);

  return <MBDContext.Provider value={mbdProps}>{children}</MBDContext.Provider>;
}

export function useMBDContext(): MBDContextType {
  const context = useContext(MBDContext);
  return context;
}
